import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { previewApInvoiceSplit, type ApInvoiceDocumentRow } from "./apInvoicesApi";
import { suggestApInvoiceDocumentGroups } from "./apInvoiceDocumentGroups";
import {
  applyApInvoiceSplitDraftDefaults,
  type ApInvoiceSplitDraftDefaults,
} from "./apInvoiceSplitDraftDefaults";
import {
  addApInvoiceSplitGroup,
  apInvoiceSplitGroupIndexForDocument,
  moveApInvoiceSplitDocument,
  removeApInvoiceSplitGroup,
} from "./apInvoiceSplitGroupEdit";

const MANUFACTURER_DUPLICATE_REMOVE_HINT =
  "This manufacturer invoice number is already in the hub. Leave this on to delete these attachments from the vendor invoice draft and skip creating another manufacturer invoice. The existing manufacturer invoice is not deleted. If the vendor invoice draft has no attachments left, that draft is deleted too. Turn this off to keep the files on the vendor invoice and enter a different manufacturer invoice number.";

export type ApInvoiceSplitModalMode = "vendor" | "manufacturer";

export interface ApInvoiceSplitConfirmGroup {
  document_ids: string[];
  vendor_invoice_number: string;
  invoice_date: string;
  mfr_invoice_number: string;
  total_cost: string;
  remove_duplicate?: boolean;
}

function buildInitialGroups(
  documents: ApInvoiceDocumentRow[],
  draftDefaults: ApInvoiceSplitDraftDefaults,
): ApInvoiceSplitConfirmGroup[] {
  const suggestion = suggestApInvoiceDocumentGroups(
    documents.map((document) => ({
      id: document.id,
      filename: document.filename,
      kind: document.kind,
    })),
  );
  if (suggestion.groups.length === 0) {
    const pdfIds = documents
      .filter((document) => document.kind === "vendor_pdf" || document.kind === "other")
      .map((document) => document.id);
    if (pdfIds.length === 0) return [];
    return applyApInvoiceSplitDraftDefaults(
      [
        {
          document_ids: pdfIds,
          vendor_invoice_number: "",
          invoice_date: "",
          mfr_invoice_number: "",
          total_cost: "0",
        },
      ],
      draftDefaults,
    );
  }
  return applyApInvoiceSplitDraftDefaults(
    suggestion.groups.map((group) => ({
      document_ids: group.document_ids,
      vendor_invoice_number: group.suggested_mfr_invoice_number ?? "",
      invoice_date: "",
      mfr_invoice_number: group.suggested_mfr_invoice_number ?? "",
      total_cost: "0",
    })),
    draftDefaults,
  );
}

const EMPTY_SPLIT_DRAFT: ApInvoiceSplitDraftDefaults = {
  vendorInvoiceNumber: "",
  invoiceDate: "",
  totalCost: "",
};

export function ApInvoiceSplitConfirmModal({
  open,
  mode,
  invoiceId,
  documents,
  draftDefaults = EMPTY_SPLIT_DRAFT,
  selectedDocumentId = null,
  onSelectDocument,
  onDiscardDocument,
  discardingDocumentId = null,
  onCancel,
  onConfirm,
  isPending,
  errorMessage = null,
}: {
  open: boolean;
  mode: ApInvoiceSplitModalMode;
  invoiceId: string;
  documents: ApInvoiceDocumentRow[];
  /** Number, date, and total already entered on the vendor invoice draft. */
  draftDefaults?: ApInvoiceSplitDraftDefaults;
  selectedDocumentId?: string | null;
  onSelectDocument?: (documentId: string) => void;
  /** Deletes marked attachments when the split is submitted, not when Discard is chosen. */
  onDiscardDocument?: (documentId: string) => Promise<void> | void;
  discardingDocumentId?: string | null;
  onCancel: () => void;
  onConfirm: (groups: ApInvoiceSplitConfirmGroup[], discardDocumentIds: string[]) => void | Promise<void>;
  isPending: boolean;
  errorMessage?: string | null;
}): JSX.Element | null {
  const [groups, setGroups] = useState<ApInvoiceSplitConfirmGroup[]>([]);
  const [pendingDiscardIds, setPendingDiscardIds] = useState<Set<string>>(() => new Set());
  const [keptDuplicateGroupIndexes, setKeptDuplicateGroupIndexes] = useState<Set<number>>(() => new Set());
  const draftDefaultsRef = useRef(draftDefaults);
  draftDefaultsRef.current = draftDefaults;
  const groupsInitializedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      groupsInitializedRef.current = false;
      return;
    }
    if (!groupsInitializedRef.current) {
      groupsInitializedRef.current = true;
      setGroups(buildInitialGroups(documents, draftDefaultsRef.current));
      setPendingDiscardIds(new Set());
      setKeptDuplicateGroupIndexes(new Set());
      return;
    }
    const liveIds = new Set(documents.map((document) => document.id));
    setGroups((current) =>
      current.map((group) => ({
        ...group,
        document_ids: group.document_ids.filter((documentId) => liveIds.has(documentId)),
      })),
    );
  }, [documents, open]);

  const previewQuery = useQuery({
    queryKey: ["accounting", "ap-invoice", invoiceId, "split-preview", mode],
    queryFn: () => previewApInvoiceSplit(invoiceId, mode),
    enabled: open && invoiceId !== "",
    staleTime: 10_000,
  });

  const documentsById = useMemo(() => new Map(documents.map((document) => [document.id, document])), [documents]);

  if (!open) return null;

  const title =
    mode === "manufacturer" ? "Move to manufacturer invoices" : "Split into vendor invoices";
  const serverWarnings = previewQuery.data?.suggestion.warnings ?? [];
  const duplicateMfrInvoiceNumbers = new Set(previewQuery.data?.duplicate_mfr_invoice_numbers ?? []);
  const assignedDocumentIds = new Set(groups.flatMap((group) => group.document_ids));
  const unassignedDocuments = documents.filter((document) => !assignedDocumentIds.has(document.id));
  const groupsWithAttachments = groups.filter((group) => group.document_ids.length > 0);

  return (
      <Card className="flex h-auto min-h-[32rem] w-full flex-col lg:h-full lg:min-h-0">
        <CardHeader className="shrink-0 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-600">
            Suggested groups come from the file names. The invoice stays open on the left, including when you open a
            date, so you can read the number, date, and total. Move an attachment if the group is wrong.
          </p>
        </CardHeader>
        <CardBody className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          {serverWarnings.length > 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="flex items-center gap-1 font-medium">
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                Review before continuing
              </p>
              <ul className="mt-1 list-disc pl-5">
                {serverWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {unassignedDocuments.length > 0 ? (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              <p className="font-medium text-slate-700">Not included</p>
              <p className="mt-1">
                These stay on this vendor invoice. Choose a group, or discard a file you do not need.
              </p>
              <ul className="mt-2 space-y-2">
                {unassignedDocuments.map((document) => (
                  <ApInvoiceSplitDocumentRow
                    key={document.id}
                    document={document}
                    groups={groups}
                    selected={selectedDocumentId === document.id}
                    onOpen={() => onSelectDocument?.(document.id)}
                    onDiscard={
                      onDiscardDocument == null
                        ? undefined
                        : () => {
                            setPendingDiscardIds((current) => {
                              const next = new Set(current);
                              next.add(document.id);
                              return next;
                            });
                          }
                    }
                    discardSelected={pendingDiscardIds.has(document.id)}
                    discardPending={discardingDocumentId === document.id}
                    onMove={(target) => {
                      setPendingDiscardIds((current) => {
                        if (!current.has(document.id)) return current;
                        const next = new Set(current);
                        next.delete(document.id);
                        return next;
                      });
                      setGroups((current) => moveApInvoiceSplitDocument(current, document.id, target));
                    }}
                  />
                ))}
              </ul>
            </div>
          ) : null}
          {groups.map((group, index) => {
            const suggestedMfrInvoiceNumber =
              previewQuery.data?.suggestion.groups[index]?.suggested_mfr_invoice_number ?? null;
            const isDuplicate =
              mode === "manufacturer" &&
              suggestedMfrInvoiceNumber != null &&
              duplicateMfrInvoiceNumbers.has(suggestedMfrInvoiceNumber);
            const removeDuplicate = isDuplicate && !keptDuplicateGroupIndexes.has(index);
            return (
            <div key={`split-group-${String(index)}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-slate-800">Group {index + 1}</p>
                {groups.length > 1 ? (
                  <button
                    type="button"
                    className="text-xs font-medium text-slate-600 hover:text-slate-900"
                    onClick={() => setGroups((current) => removeApInvoiceSplitGroup(current, index))}
                  >
                    Remove group
                  </button>
                ) : null}
              </div>
              {isDuplicate ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <p className="flex items-center gap-1 font-medium">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                    Manufacturer invoice {suggestedMfrInvoiceNumber} already exists and stays as-is.
                  </p>
                  <div className="mt-2 flex items-start gap-1.5">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={removeDuplicate}
                      className="inline-flex items-center gap-2 text-left font-medium"
                      onClick={() => {
                        setKeptDuplicateGroupIndexes((current) => {
                          const next = new Set(current);
                          if (next.has(index)) next.delete(index);
                          else next.add(index);
                          return next;
                        });
                      }}
                    >
                      <span
                        className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
                          removeDuplicate ? "bg-amber-600" : "bg-slate-300"
                        }`}
                        aria-hidden
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                            removeDuplicate ? "translate-x-[1.125rem]" : "translate-x-0.5"
                          }`}
                        />
                      </span>
                      Remove these files from the vendor invoice draft
                    </button>
                    <HoverHintWithGlyph hoverHint={MANUFACTURER_DUPLICATE_REMOVE_HINT} />
                  </div>
                </div>
              ) : null}
              {group.document_ids.length === 0 ? (
                <p className="text-xs text-slate-500">No attachments yet. Choose this group from an attachment.</p>
              ) : (
                <ul className="space-y-2 text-xs text-slate-600">
                  {group.document_ids.map((documentId) => {
                    const document = documentsById.get(documentId);
                    if (document == null) return null;
                    return (
                      <ApInvoiceSplitDocumentRow
                        key={documentId}
                        document={document}
                        groups={groups}
                        selected={selectedDocumentId === documentId}
                        onOpen={() => onSelectDocument?.(documentId)}
                        onMove={(target) =>
                          setGroups((current) => moveApInvoiceSplitDocument(current, documentId, target))
                        }
                      />
                    );
                  })}
                </ul>
              )}
              {mode === "vendor" ? (
                <>
                  <div>
                    <Label htmlFor={`split-vendor-number-${index}`}>Vendor invoice number</Label>
                    <Input
                      id={`split-vendor-number-${index}`}
                      value={group.vendor_invoice_number}
                      onChange={(event) => {
                        const next = [...groups];
                        next[index] = { ...group, vendor_invoice_number: event.target.value };
                        setGroups(next);
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`split-vendor-date-${index}`}>Invoice date</Label>
                    <Input
                      id={`split-vendor-date-${index}`}
                      type="date"
                      value={group.invoice_date}
                      onChange={(event) => {
                        const next = [...groups];
                        next[index] = { ...group, invoice_date: event.target.value };
                        setGroups(next);
                      }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor={`split-mfr-number-${index}`}>Manufacturer invoice number</Label>
                    <Input
                      id={`split-mfr-number-${index}`}
                      value={group.mfr_invoice_number}
                      disabled={removeDuplicate}
                      onChange={(event) => {
                        const next = [...groups];
                        next[index] = { ...group, mfr_invoice_number: event.target.value };
                        setGroups(next);
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`split-mfr-date-${index}`}>Invoice date</Label>
                    <Input
                      id={`split-mfr-date-${index}`}
                      type="date"
                      value={group.invoice_date}
                      disabled={removeDuplicate}
                      onChange={(event) => {
                        const next = [...groups];
                        next[index] = { ...group, invoice_date: event.target.value };
                        setGroups(next);
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor={`split-mfr-total-${index}`}>Total cost</Label>
                    <Input
                      id={`split-mfr-total-${index}`}
                      inputMode="decimal"
                      value={group.total_cost}
                      disabled={removeDuplicate}
                      onChange={(event) => {
                        const next = [...groups];
                        next[index] = { ...group, total_cost: event.target.value };
                        setGroups(next);
                      }}
                    />
                  </div>
                </>
              )}
            </div>
            );
          })}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setGroups((current) => addApInvoiceSplitGroup(current))}
          >
            Add group
          </Button>
        </CardBody>
        {errorMessage != null && errorMessage !== "" ? (
          <p className="shrink-0 border-t border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800" role="alert">
            {errorMessage}
          </p>
        ) : null}
        <div className="flex shrink-0 justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <Button type="button" variant="secondary" disabled={isPending} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isPending || groupsWithAttachments.length === 0}
            isLoading={isPending}
            onClick={() =>
              void onConfirm(
                groups
                  .map((group, index) => {
                    const suggestedMfrInvoiceNumber =
                      previewQuery.data?.suggestion.groups[index]?.suggested_mfr_invoice_number ?? null;
                    return {
                      ...group,
                      remove_duplicate:
                        mode === "manufacturer" &&
                        suggestedMfrInvoiceNumber != null &&
                        duplicateMfrInvoiceNumbers.has(suggestedMfrInvoiceNumber) &&
                        !keptDuplicateGroupIndexes.has(index),
                    };
                  })
                  .filter((group) => group.document_ids.length > 0),
                [...pendingDiscardIds],
              )
            }
          >
            {mode === "manufacturer" ? "Move to manufacturer invoices" : "Split invoices"}
          </Button>
        </div>
    </Card>
  );
}

function ApInvoiceSplitDocumentRow({
  document,
  groups,
  selected,
  onOpen,
  onDiscard,
  discardSelected = false,
  discardPending = false,
  onMove,
}: {
  document: ApInvoiceDocumentRow;
  groups: ApInvoiceSplitConfirmGroup[];
  selected: boolean;
  onOpen: () => void;
  onDiscard?: () => void;
  discardSelected?: boolean;
  discardPending?: boolean;
  onMove: (target: number | "new" | "none") => void;
}) {
  const currentIndex = apInvoiceSplitGroupIndexForDocument(groups, document.id);
  const selectValue = discardSelected ? "discard" : currentIndex == null ? "none" : String(currentIndex);
  return (
    <li className="flex items-center justify-between gap-3">
      <button
        type="button"
        className={
          selected
            ? "min-w-0 break-all text-left font-medium text-slate-900 underline"
            : "min-w-0 break-all text-left text-brand-700 hover:underline"
        }
        onClick={onOpen}
      >
        {document.filename}
      </button>
      <select
        aria-label={`Group for ${document.filename}`}
        className="h-8 max-w-[11rem] shrink-0 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800"
        value={selectValue}
        disabled={discardPending}
        onChange={(event) => {
          const next = event.target.value;
          if (next === "discard") {
            onDiscard?.();
            return;
          }
          if (next === "none") {
            onMove("none");
            return;
          }
          if (next === "new") {
            onMove("new");
            return;
          }
          onMove(Number.parseInt(next, 10));
        }}
      >
        <option value="none">Not included</option>
        {groups.map((_, index) => (
          <option key={index} value={String(index)}>
            Group {index + 1}
          </option>
        ))}
        <option value="new">New group</option>
        {onDiscard != null && (currentIndex == null || discardSelected) ? (
          <option value="discard">Discard</option>
        ) : null}
      </select>
    </li>
  );
}
